import { apiClient } from "@/http";
import type { MemberRef } from "./member";
import { apiRoutes } from "./apiRoutes";

// 클러스터 GPU 가용량 스냅샷. 로그인한 조직 멤버는 조회하면 200 을 받는다. 모든 workspace 의
// 점유가 그대로 보인다. GPU 는 공용 자원이라 이것은 의도된 설계다.
// upstream 의 capacity 판정(status)·노드풀 목록·최장 대기 시간은 받지 않는다. 이 값들은 오해를
// 일으켰다. 예를 들어 holder(예열) pod 의 스케일업 실패도 "재고 없음"으로 판정했다. 그래서 화면에서
// 제거했다.
export type TrainingGpuCapacity = {
  ready_gpu_nodes: number;
  total_allocatable_gpus: number;
  // 서버가 계산한 여유. FE 에서 다시 계산하지 않는다. 정의상 여유에서 holder 점유는 빼지 않는다.
  // 그래서 holder_running_gpus 를 추가로 빼면 실제보다 적게 표시된다.
  free_gpus: number;
  workload_running_gpus: number;
  workload_pending_gpus: number;
  // 노드를 미리 확보하는 예열 자리. 학습이 들어오면 자리를 내준다. 그래서 여유에 포함된다.
  holder_running_gpus: number;
  holder_pending_gpus: number;
  // 승인된 예약이 다른 workspace 가 쓰지 못하게 확보한 몫. 여유에서 빠진다.
  reserved_held_aside_gpus: number;
  // 회수·종료 예정으로 펜스된 노드(cordon 또는 retiring taint)에 남은 빈 GPU. 이 GPU 는 새 작업을
  // 받지 못한다. 그래서 여유에서는 빠진다. 노드가 아직 살아 있어서 전체에는 포함된다.
  // 전체 = 여유 + 실행 중 + 예약 + 이 값 + unready_idle_gpus 이다.
  retiring_idle_gpus: number;
  // 같은 이유로 여유에서 빠지지만 원인이 다른 몫. 상태가 정상이 아닌(NotReady) 노드에 남은 빈 GPU다.
  // 종료 예정은 계획된 회수이고, 이 값은 장애다. 사용자가 할 일이 달라서 따로 표시한다.
  unready_idle_gpus: number;
  // 대기열 두 축. pending_jobs 는 k8s Workload 다. pending_sessions 는 아직 Workload 를 만들지
  // 못한 이전 단계다. 두 값은 다른 단계라 합치면 잘못된 수가 된다.
  pending_jobs: number;
  pending_sessions: number;
  // 비어 있지 않으면 이 응답의 수치가 불완전하다. upstream 이 읽지 못한 값은 0 으로 온다.
  // 그래서 0 을 "없음"으로 단정하면 안 된다.
  errors: string[];
};

// 이 응답의 수치를 그대로 믿어도 되는지 판정한다. `errors` 판정은 이 함수만 소유한다.
//
// upstream 이 읽지 못한 값은 0 으로 온다. 그래서 불완전한 응답에서는 "여유 0" 과 "정말 없음"을
// 구분할 수 없다. 표시하는 쪽은 이 판정을 따로 하지 않고 이 함수를 쓴다.
// 조회 자체가 실패한 경우(직전 값 표시·재시도)는 다른 계약이라 여기서 다루지 않는다.
export function isCapacityIncomplete(capacity: Pick<TrainingGpuCapacity, "errors">): boolean {
  return capacity.errors.length > 0;
}

export type TrainingGpuCapacityQuery = {
  // upstream 캐시를 건너뛰고 GKE·k8s 를 직접 읽는다. 사람이 누른 새로고침에만 쓴다.
  fresh?: boolean;
};

export async function getTrainingGpuCapacity(
  query: TrainingGpuCapacityQuery = {},
): Promise<TrainingGpuCapacity> {
  const response = await apiClient.get<TrainingGpuCapacity>(apiRoutes.training.gpuCapacity, {
    // 평소 폴링에 fresh 가 섞이지 않도록, 참일 때만 파라미터를 보낸다.
    params: query.fresh === true ? { fresh: true } : undefined,
  });
  return response.data;
}

// 일별 집계 두 개가 함께 쓰는 기간이다. 둘 다 필수다(aware UTC ISO). 서버는 `end <= start` 와
// 92일을 넘는 기간을 422 로 거절한다. 버킷은 UTC 일이다. 그래서 `start` 를 UTC 자정에 맞추면
// 첫 일이 온전한 하루가 된다.
export type TrainingGpuUsageWindowQuery = {
  start: string;
  end: string;
};

// 클러스터 점유 하루치다. `period` 는 UTC 일(`YYYY-MM-DD`)이다. `held` 는 그날 클러스터가 가진
// GPU 의 시간 합이고, `used` 는 workload 가 요청한 GPU 의 시간 합이다. 값은 5분 간격 표본을
// 적분한 것이다. attempt 실행 구간으로 센 workspace 사용량과 출처가 다르다. 그래서 두 값의
// 합은 서로 같지 않다.
export type TrainingGpuClusterUsageDay = {
  period: string;
  held_gpu_hours: number;
  used_gpu_hours: number;
  idle_gpu_hours: number;
  // `used / held`(0..1). `held` 가 0 인 날은 null 이다. 0 은 실제로 쓰지 않은 날이다.
  utilization: number | null;
  // `held` 에 단가를 곱한 값(USD)이다. upstream 에 단가가 없으면 null 이다.
  cost: number | null;
};

export type TrainingGpuClusterUsage = {
  start: string;
  end: string;
  generated_at: string | null;
  // 기간에 걸친 모든 UTC 일이다. `days` 는 표본이 있는 날만 담는다. 여기에 있고 `days` 에 없는
  // 날은 점유가 0 인 날이 아니라 표본이 없는 날이다.
  periods: string[];
  // 아래 합계는 `days` 의 합이다. `utilization` 은 합계의 `used / held` 다.
  held_gpu_hours: number;
  used_gpu_hours: number;
  idle_gpu_hours: number;
  utilization: number | null;
  cost: number | null;
  days: TrainingGpuClusterUsageDay[];
};

export async function getTrainingGpuClusterUsage(
  query: TrainingGpuUsageWindowQuery,
  options: { signal?: AbortSignal } = {},
): Promise<TrainingGpuClusterUsage> {
  const response = await apiClient.get<TrainingGpuClusterUsage>(
    apiRoutes.training.gpuUsageCluster,
    { params: { start: query.start, end: query.end }, signal: options.signal },
  );
  return response.data;
}

// 사용량 분해의 한 조각이다. 계층 하나 또는 작업 종류 하나의 몫이다. `key` 는 upstream 값이다.
// 계층은 `ondemand`·`flex`·`spot`·`unknown`, 작업 종류는 `train`·`eval`·`preprocess` 가 온다.
// 닫힌 어휘가 아니다. 모르는 값도 그대로 표시한다.
export type TrainingGpuUsageShare = {
  key: string;
  gpu_hours: number;
  cost: number | null;
};

// 기간 안에서 한 workspace 가 쓴 GPU 시간과 그 분해다. `daily_gpu_hours` 의 합, `by_type` 의 합,
// `by_tier` 의 합은 모두 `gpu_hours` 와 같다.
export type TrainingGpuWorkspaceUsageRow = {
  workspace_id: string;
  // registry 에 별칭이 없으면 null 이다. 그러면 호출부가 표시명을 UUID 로 폴백한다.
  display_name: string | null;
  gpu_hours: number;
  // 사용분에 단가를 곱한 값(USD)이다. upstream 에 단가가 없으면 null 이다.
  cost: number | null;
  session_count: number;
  // 응답의 `periods` 와 같은 순서, 같은 길이다. 사용이 없는 날은 0 이다.
  daily_gpu_hours: number[];
  by_type: TrainingGpuUsageShare[];
  by_tier: TrainingGpuUsageShare[];
};

export type TrainingGpuWorkspaceUsage = {
  start: string;
  end: string;
  generated_at: string | null;
  periods: string[];
  // `workspaces` 의 합이다. `by_type`·`by_tier` 는 전 workspace 를 합친 분해다.
  total_gpu_hours: number;
  total_cost: number | null;
  by_type: TrainingGpuUsageShare[];
  by_tier: TrainingGpuUsageShare[];
  // 많이 쓴 순이다. GPU 를 쓰지 않은 workspace 는 없다.
  workspaces: TrainingGpuWorkspaceUsageRow[];
};

export async function getTrainingGpuWorkspaceUsage(
  query: TrainingGpuUsageWindowQuery,
  options: { signal?: AbortSignal } = {},
): Promise<TrainingGpuWorkspaceUsage> {
  const response = await apiClient.get<TrainingGpuWorkspaceUsage>(
    apiRoutes.training.gpuUsageWorkspaces,
    { params: { start: query.start, end: query.end }, signal: options.signal },
  );
  return response.data;
}

// 요일·시간대별 한도 한 구간. `days` 는 월=0 … 일=6 이다. `start_time`/`end_time` 은 `"09:00"`
// 형식의 **로컬 wall-clock** 이다. 기준 시간대는 같은 행의 `timezone` 이다. 그래서 UTC 로
// 환산하면 틀린다. `gpu_limit` 이 null 이면 그 구간은 무제한이다.
export type TrainingGpuQuotaScheduleEntry = {
  name: string;
  days: number[];
  start_time: string;
  end_time: string;
  gpu_limit: number | null;
};

// workspace 한 곳의 GPU 한도와 현재 점유. 사람 행(TrainingGpuMemberQuota)의 내역 한 줄이다.
export type TrainingGpuWorkspaceQuota = {
  workspace_id: string;
  group_id: string | null;
  // upstream workspace 이름이라 항상 온다. usage 행의 display_name 은 별칭이 없으면 null 이라
  // 이 값과 다르다.
  name: string;
  // 기준 한도. 스케줄이 적용되어 있으면 지금 적용되는 값은 `effective_gpu_limit` 이다.
  gpu_limit: number | null;
  effective_gpu_limit: number | null;
  // 지금 효력이 있는 임시 증액의 GPU 수. 없으면 null 이다. effective_gpu_limit 에 이미 들어 있는
  // 값이라 더하지 않는다. 유효 한도가 스케줄 한도보다 높은 이유를 말할 때 쓴다.
  active_grant_gpus: number | null;
  // 소속 group 이 적용하는 상한. workspace 한도가 이보다 커도 실제 한도는 이 값이다.
  group_gpu_max_limit: number | null;
  timezone: string | null;
  schedule: TrainingGpuQuotaScheduleEntry[];
  // 지금 사용 중인 GPU. active 는 running+pending 의 합이 아니라 서버가 따로 계산한 값이다.
  active_gpus: number;
  running_gpus: number;
  pending_gpus: number;
  reserved_gpus: number;
};

// 한 사람이 선점한 workspace 들의 한도·점유를 합친 값. upstream 에는 사람 단위 한도가 없고,
// 한도는 workspace 에 있다. 그래서 이 행은 **선점 관계로 만든 요약이다. 누군가에게 적용된 한도가
// 아니다.** 선점은 배타적이다(workspace 당 최대 한 명). 그래서 한 workspace 는 한 행에만 센다.
export type TrainingGpuMemberQuota = {
  // null 이면 아무도 선점하지 않은 workspace 를 모은 미할당 행이다. 응답에서 최대 한 줄이고 항상
  // 마지막에 온다. 선점되지 않은 workspace 가 없으면 이 행은 없다.
  member: MemberRef | null;
  // 선점 workspace 들의 한도 합. 하나라도 무제한(null)이면 합계도 무제한이다. 유한한 값만 더하면
  // 총합 자리에 실제 총합보다 작은 값이 표시된다.
  gpu_limit: number | null;
  effective_gpu_limit: number | null;
  active_gpus: number;
  running_gpus: number;
  pending_gpus: number;
  reserved_gpus: number;
  active_attempts: number;
  running_attempts: number;
  pending_attempts: number;
  waiting_attempts: number;
  // 이 행을 이루는 workspace 내역. 이름순이고 비어 있지 않다. group 상한·시간대·스케줄·점유
  // 출처는 합치면 뜻을 잃는 축이다. 그래서 사람 행이 아니라 여기에만 있다.
  workspaces: TrainingGpuWorkspaceQuota[];
};

export async function listTrainingGpuMemberQuotas(
  options: { signal?: AbortSignal } = {},
): Promise<TrainingGpuMemberQuota[]> {
  const response = await apiClient.get<TrainingGpuMemberQuota[]>(
    apiRoutes.training.gpuMemberQuotas,
    { signal: options.signal },
  );
  return response.data;
}
